/**
 * Collection Tabs Carousel
 * CSS-only carousel with drag/scroll functionality
 * Optimized for performance with deferred loading
 */

class CollectionTabsCarousel {
  constructor() {
    this.carousels = document.querySelectorAll('.css-collection-carousel');
    this.section = document.querySelector('.collection-tabs-carousel');
    this.tabButtons = [];
    this.tabPanels = [];

    if (this.section) {
      this.tabButtons = this.section.querySelectorAll('[role="tab"]');
      this.tabPanels = this.section.querySelectorAll('[role="tabpanel"]');
    }

    this.init();
  }

  init() {
    if (this.carousels.length === 0) return;

    this.initCarousels();
    this.initDeferredImageLoading();
  }

  initCarousels() {
    this.carousels.forEach(carousel => {
      let isDragging = false;
      let startX;
      let startY;
      let scrollLeft;
      let velocity = 0;
      let lastX = 0;
      let lastTime = 0;
      let momentumId = null;

      carousel.addEventListener('mousedown', (e) => {
        // Don't interfere with buttons/inputs
        if (e.target.closest('button:not(.product-card__wishlist), input, select, textarea')) return;

        isDragging = false; // Start as false, only set true when actually moving
        startX = e.pageX;
        startY = e.pageY;
        scrollLeft = carousel.scrollLeft;
        velocity = 0;
        lastX = e.pageX;
        lastTime = Date.now();

        // Stop any existing momentum
        if (momentumId) {
          cancelAnimationFrame(momentumId);
          momentumId = null;
        }

        carousel.style.scrollBehavior = 'auto';
      });

      document.addEventListener('mousemove', (e) => {
        if (!startX) return; // No mousedown happened

        const moveX = e.pageX - startX;
        const moveY = e.pageY - startY;
        const distanceX = Math.abs(moveX);
        const distanceY = Math.abs(moveY);

        // Start dragging if we've moved past threshold and it's more horizontal than vertical
        if (!isDragging && distanceX > 5 && distanceX > distanceY) {
          isDragging = true;
          carousel.style.cursor = 'grabbing';
          carousel.classList.add('dragging');
        }

        if (!isDragging) return;

        e.preventDefault();

        // Calculate velocity for momentum
        const now = Date.now();
        const deltaTime = now - lastTime;
        if (deltaTime > 0) {
          velocity = (e.pageX - lastX) / deltaTime;
        }
        lastX = e.pageX;
        lastTime = now;

        // Scroll the carousel
        const walk = (startX - e.pageX) * 2;
        carousel.scrollLeft = scrollLeft + walk;
      });

      document.addEventListener('mouseup', () => {
        if (!startX) return; // No mousedown happened

        if (isDragging) {
          isDragging = false;
          carousel.style.cursor = 'grab';
          carousel.classList.remove('dragging');

          // Apply momentum if velocity is significant
          if (Math.abs(velocity) > 0.5) {
            const applyMomentum = () => {
              if (Math.abs(velocity) < 0.05) {
                momentumId = null;
                carousel.style.scrollBehavior = 'smooth';
                return;
              }

              carousel.scrollLeft -= velocity * 16;
              velocity *= 0.90; // Deceleration
              momentumId = requestAnimationFrame(applyMomentum);
            };
            applyMomentum();
          } else {
            carousel.style.scrollBehavior = 'smooth';
          }
        }

        // Reset tracking variables
        startX = null;
        startY = null;
      });

      // Prevent clicks only during momentum (not during regular dragging)
      carousel.addEventListener('click', (e) => {
        if (momentumId) {
          e.preventDefault();
          e.stopPropagation();
        }
      }, true);

      // Prevent text selection
      carousel.addEventListener('selectstart', (e) => {
        if (isDragging) e.preventDefault();
      });

      // Prevent default drag on images
      carousel.addEventListener('dragstart', (e) => {
        e.preventDefault();
      });
    });
  }

  initDeferredImageLoading() {
    if (!this.section || this.tabButtons.length === 0) return;

    // Preload next tab on hover (predictive loading)
    this.tabButtons.forEach((button, index) => {
      button.addEventListener('mouseenter', function() {
        const panel = this.tabPanels[index];
        if (panel && panel.getAttribute('data-images-loaded') === 'false') {
          this.loadTabImages(panel);
        }
      }.bind(this), { once: true, passive: true });

      // Load on click (fallback for touch devices)
      button.addEventListener('click', function() {
        const panel = this.tabPanels[index];
        if (panel && panel.getAttribute('data-images-loaded') === 'false') {
          this.loadTabImages(panel);
        }
      }.bind(this), { passive: true });
    });

    // Intersection Observer for progressive loading (when tab comes into view)
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && entry.target.getAttribute('data-images-loaded') === 'false') {
            this.loadTabImages(entry.target);
          }
        });
      }, { rootMargin: '50px' });

      this.tabPanels.forEach(panel => {
        if (panel.getAttribute('data-images-loaded') === 'false') {
          observer.observe(panel);
        }
      });
    }
  }

  loadTabImages(panel) {
    const images = panel.querySelectorAll('img[loading="lazy"]');
    images.forEach(img => {
      img.loading = 'eager';
      img.fetchpriority = 'high';
    });
    panel.setAttribute('data-images-loaded', 'true');
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
  // Use requestIdleCallback for non-critical initialization
  if ('requestIdleCallback' in window) {
    requestIdleCallback(() => {
      new CollectionTabsCarousel();
    });
  } else {
    // Fallback for browsers without requestIdleCallback
    setTimeout(() => {
      new CollectionTabsCarousel();
    }, 0);
  }
});
